// =====================================================
// 产品矩阵管理系统 - 常量配置
// 集中管理所有常量，便于维护和复用
// 参考 Excalidraw 的常量管理模式
// =====================================================

import type { LayoutType, StyleType, ExportFormat } from './types';

// =====================================================
// 布局配置
// =====================================================

/** 预设布局配置 */
export const LAYOUT_CONFIGS: Record<
  Exclude<LayoutType, 'grid_custom'>,
  { rows: number; cols: number; gapRatio: number; label: string }
> = {
  grid_2x2: { rows: 2, cols: 2, gapRatio: 0.02, label: '2×2' },
  grid_3x3: { rows: 3, cols: 3, gapRatio: 0.015, label: '3×3' },
  grid_4x4: { rows: 4, cols: 4, gapRatio: 0.012, label: '4×4' },
  grid_9x9: { rows: 9, cols: 9, gapRatio: 0.008, label: '9×9' },
} as const;

/** 自定义布局配置 */
export const CUSTOM_LAYOUT_CONFIG = {
  minRows: 1,
  maxRows: 12,
  minCols: 1,
  maxCols: 12,
  defaultGapRatio: 0.01,
} as const;

/** 默认布局类型 */
export const DEFAULT_LAYOUT_TYPE: LayoutType = 'grid_3x3';

/** 默认风格类型 */
export const DEFAULT_STYLE_TYPE: StyleType = 'standard';

// =====================================================
// 画布尺寸配置
// =====================================================

/** 画布容器配置 */
export const CANVAS_CONFIG = {
  /** 最大画布尺寸 (px) */
  maxSize: 800,
  /** 默认背景色 */
  defaultBackgroundColor: '#ffffff',
  /** 默认内边距 */
  defaultPadding: 20,
  /** 默认格子间距 */
  defaultGap: 10,
} as const;

/** 单元格配置 */
export const CELL_CONFIG = {
  /** 标准单格分辨率 */
  standardResolution: 1080,
  /** 图片区域高度比例 (标准布局) */
  imageHeightRatio: {
    compact: 0.70,   // 9x9
    medium: 0.65,    // 4x4
    large: 0.55,     // 2x2, 3x3
  },
} as const;

// =====================================================
// 导出配置
// =====================================================

/** 导出格式配置 */
export const EXPORT_FORMATS: Record<ExportFormat, {
  mimeType: string;
  extension: string;
  label: string;
  description: string;
  supportsQuality: boolean;
}> = {
  png: {
    mimeType: 'image/png',
    extension: 'png',
    label: 'PNG',
    description: '无损',
    supportsQuality: false,
  },
  jpg: {
    mimeType: 'image/jpeg',
    extension: 'jpg',
    label: 'JPG',
    description: '较小',
    supportsQuality: true,
  },
  webp: {
    mimeType: 'image/webp',
    extension: 'webp',
    label: 'WebP',
    description: '最小',
    supportsQuality: true,
  },
} as const;

/** 导出预设配置 */
export const EXPORT_PRESETS = {
  /** 社交媒体 (微信朋友圈等) */
  social: {
    scale: 2,
    quality: 0.9,
    format: 'jpg' as ExportFormat,
  },
  /** 高清打印 */
  print: {
    scale: 3,
    quality: 1,
    format: 'png' as ExportFormat,
  },
  /** 网页展示 */
  web: {
    scale: 1,
    quality: 0.85,
    format: 'webp' as ExportFormat,
  },
} as const;

/** 导出分辨率选项 */
export const EXPORT_SCALE_OPTIONS = [
  { value: 1, label: '1x', description: '标准' },
  { value: 2, label: '2x', description: '高清', recommended: true },
  { value: 3, label: '3x', description: '超清' },
] as const;

/** 默认导出选项 */
export const DEFAULT_EXPORT_OPTIONS = {
  format: 'png' as ExportFormat,
  quality: 90,
  scale: 2,
  includeWatermark: false,
  padding: 10,
} as const;

// =====================================================
// 水印配置
// =====================================================

/** 水印位置选项 */
export const WATERMARK_POSITIONS = [
  { value: 'bottom-right', label: '右下' },
  { value: 'bottom-left', label: '左下' },
  { value: 'top-right', label: '右上' },
  { value: 'top-left', label: '左上' },
] as const;

/** 默认水印配置 */
export const DEFAULT_WATERMARK_CONFIG = {
  enabled: false,
  text: 'LED Display Solutions',
  position: 'bottom-right' as const,
  opacity: 0.3,
} as const;

// =====================================================
// 字体配置
// =====================================================

/** 字体大小配置 */
export const FONT_SIZES = {
  xs: 8,
  sm: 10,
  md: 12,
  lg: 14,
  xl: 18,
  '2xl': 24,
} as const;

/** 动态字体计算系数 */
export const FONT_SCALE_FACTORS = {
  title: 0.08,      // 标题字体 = cellSize * 0.08
  subtitle: 0.06,   // 副标题 = cellSize * 0.06
  small: 0.05,      // 小字体 = cellSize * 0.05
  minTitle: 10,     // 标题最小值
  maxTitle: 18,     // 标题最大值
  minSubtitle: 8,   // 副标题最小值
  maxSubtitle: 14,  // 副标题最大值
} as const;

// =====================================================
// 风格配置
// =====================================================

/** 风格样式配置 */
export const STYLE_CONFIGS: Record<StyleType, {
  label: string;
  description: string;
  imagePosition: 'top' | 'left' | 'diagonal' | 'center' | 'split';
}> = {
  standard: {
    label: '标准布局',
    description: '图片上方，文字下方，经典展示风格',
    imagePosition: 'top',
  },
  split: {
    label: '左右分割',
    description: '左侧图片，右侧文字，适合横向展示',
    imagePosition: 'left',
  },
  diagonal: {
    label: '对角线动感',
    description: '斜切效果，渐变背景，科技感强',
    imagePosition: 'diagonal',
  },
  perspective: {
    label: '3D透视',
    description: '3D透视效果，立体感强',
    imagePosition: 'center',
  },
  asymmetric: {
    label: '非对称重叠',
    description: '非对称布局，视觉冲击力强',
    imagePosition: 'split',
  },
  magazine: {
    label: '杂志风格',
    description: '彩色边条+标签，类似杂志排版',
    imagePosition: 'top',
  },
  minimal: {
    label: '极简留白',
    description: '大量留白，居中展示，简洁清爽',
    imagePosition: 'center',
  },
} as const;

/** 杂志风格的强调色 */
export const MAGAZINE_ACCENT_COLORS = [
  '#3B82F6', // blue
  '#10B981', // green
  '#F59E0B', // amber
  '#EF4444', // red
  '#8B5CF6', // purple
] as const;

// =====================================================
// 历史记录配置
// =====================================================

/** 历史记录配置 */
export const HISTORY_CONFIG = {
  /** 最大撤销步数 */
  maxUndoSteps: 100,
  /** 最大重做步数 */
  maxRedoSteps: 100,
  /** 防抖延迟 (ms) - 设为 0 表示每次操作都记录 */
  debounceDelay: 0,
} as const;

// =====================================================
// 拖拽配置
// =====================================================

/** 拖拽配置 */
export const DRAG_CONFIG = {
  /** 拖拽阈值 (px) */
  threshold: 10,
  /** 拖拽延迟 (ms) */
  delay: 0,
  /** 节流间隔 (ms) */
  throttleInterval: 16, // ~60fps
} as const;

// =====================================================
// 动画配置
// =====================================================

/** 动画时长配置 */
export const ANIMATION_DURATION = {
  fast: 150,
  normal: 300,
  slow: 500,
} as const;

// =====================================================
// 键盘快捷键
// =====================================================

/** 键盘快捷键配置 */
export const KEYBOARD_SHORTCUTS = {
  undo: { key: 'z', ctrlKey: true, description: '撤销' },
  redo: { key: 'z', ctrlKey: true, shiftKey: true, description: '重做' },
  redoAlt: { key: 'y', ctrlKey: true, description: '重做' },
  delete: { key: 'Delete', description: '删除选中' },
  deleteAlt: { key: 'Backspace', description: '删除选中' },
  selectAll: { key: 'a', ctrlKey: true, description: '全选' },
  export: { key: 'e', ctrlKey: true, description: '导出' },
  save: { key: 's', ctrlKey: true, description: '保存' },
} as const;

// =====================================================
// 错误消息
// =====================================================

/** 错误消息常量 */
export const ERROR_MESSAGES = {
  CANVAS_NOT_FOUND: '找不到画布元素',
  EXPORT_FAILED: '导出失败',
  IMAGE_LOAD_FAILED: '图片加载失败',
  NETWORK_ERROR: '网络错误，请重试',
  UNKNOWN_ERROR: '未知错误',
} as const;

// =====================================================
// 工具函数
// =====================================================

/**
 * 获取布局配置
 */
export function getLayoutConfig(
  layoutType: LayoutType,
  customRows?: number,
  customCols?: number
): { rows: number; cols: number; gapRatio: number } {
  if (layoutType === 'grid_custom') {
    return {
      rows: customRows ?? 3,
      cols: customCols ?? 3,
      gapRatio: CUSTOM_LAYOUT_CONFIG.defaultGapRatio,
    };
  }
  return LAYOUT_CONFIGS[layoutType];
}

/**
 * 计算动态字体大小
 */
export function calculateFontSize(
  cellSize: number,
  type: 'title' | 'subtitle' | 'small'
): number {
  const factor = FONT_SCALE_FACTORS[type];
  const calculated = cellSize * factor;
  
  if (type === 'title') {
    return Math.max(FONT_SCALE_FACTORS.minTitle, Math.min(FONT_SCALE_FACTORS.maxTitle, calculated));
  }
  if (type === 'subtitle') {
    return Math.max(FONT_SCALE_FACTORS.minSubtitle, Math.min(FONT_SCALE_FACTORS.maxSubtitle, calculated));
  }
  return Math.max(FONT_SIZES.xs, calculated);
}

/**
 * 获取杂志风格的强调色
 */
export function getMagazineAccentColor(seed: string): string {
  const charCode = seed.charCodeAt(0) || 0;
  const index = charCode % MAGAZINE_ACCENT_COLORS.length;
  return MAGAZINE_ACCENT_COLORS[index];
}
